import {Injectable, Provider} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';

import {ConnectConnectionsService, ConnectServerConnection} from '../../../services/connect/connect-connections.service';
import {
  ConnectNodeCatalog,
  ConnectNodesService,
} from '../../../services/connect/connect-nodes.service';
import {ConnectProviderCatalog, ConnectProvidersService} from '../../../services/connect/connect-providers.service';
import {
  ConnectRun,
  ConnectRunContext,
  ConnectRunReplaySummary,
  ConnectWorkflow,
  ConnectWorkflowRun,
  ConnectWorkflowsService,
} from '../../../services/connect/connect-workflows.service';
import {
  ConnectWorkflowCanvasVisualFixtureState,
  readConnectWorkflowCanvasVisualFixture,
} from './connect-workflow-canvas-visual-fixture.guard';

const fixtureWorkflow: ConnectWorkflow = {
  id: 'fixture-debug-workflow',
  name: 'Review-safe workflow debug fixture',
  tags: ['fixture', 'debug-review'],
  nodes: [
    {
      id: 'node-trigger',
      type: 'trigger.manual',
      ref: 'fixture.manual',
      label: 'Manual trigger',
      ['x-ui']: {position: {x: 100, y: 140}},
    },
    {
      id: 'node-hubspot',
      type: 'api.connector',
      ref: 'hubspot.contacts.upsert',
      label: 'Upsert contact',
      connection: 'hubspot-prod',
      ['x-ui']: {position: {x: 360, y: 140}},
    },
    {
      id: 'node-transform',
      type: 'control.transform',
      ref: 'system.normalize_payload',
      label: 'Normalize payload',
      ['x-ui']: {position: {x: 640, y: 140}},
    },
    {
      id: 'node-branch',
      type: 'control.condition',
      ref: 'system.branch_on_status',
      label: 'Branch on CRM response',
      ['x-ui']: {position: {x: 900, y: 140}},
    },
    {
      id: 'node-success',
      type: 'api.connector',
      ref: 'slack.messages.send',
      label: 'Notify success',
      ['x-ui']: {position: {x: 1180, y: 80}},
    },
    {
      id: 'node-failure',
      type: 'api.http',
      ref: 'http.retry_webhook',
      label: 'Escalate failure',
      ['x-ui']: {position: {x: 1180, y: 220}},
    },
  ],
  edges: [
    {id: 'edge-trigger-hubspot', source: 'node-trigger', target: 'node-hubspot', source_handle: 'out', target_handle: 'in'},
    {id: 'edge-hubspot-transform', source: 'node-hubspot', target: 'node-transform', source_handle: 'out', target_handle: 'in'},
    {id: 'edge-transform-branch', source: 'node-transform', target: 'node-branch', source_handle: 'out', target_handle: 'in'},
    {id: 'edge-branch-success', source: 'node-branch', target: 'node-success', source_handle: 'out', target_handle: 'in', label: 'success'},
    {id: 'edge-branch-failure', source: 'node-branch', target: 'node-failure', source_handle: 'out', target_handle: 'in', label: 'failed'},
  ],
  ['x-ui']: {
    backgrounds: [
      {
        id: 'bg-retrieval',
        label: 'CRM enrichment',
        position: {x: 60, y: 60},
        size: {width: 580, height: 280},
        style: {fill: '#dbeafe', stroke: '#93c5fd', opacity: 0.3, radius: 18},
      },
      {
        id: 'bg-processing',
        label: 'Decisioning',
        position: {x: 620, y: 60},
        size: {width: 620, height: 280},
        style: {fill: '#ede9fe', stroke: '#c4b5fd', opacity: 0.28, radius: 18},
      },
    ],
  },
};

const fixtureProvidersCatalog: ConnectProviderCatalog = {
  version: 'v1',
  providers: [
    {
      id: 'hubspot',
      name: 'HubSpot',
      ref: 'hubspot',
      description: 'Fixture provider for contact sync review.',
      auth: {supported: ['oauth'], default: 'oauth'},
      palette: {
        color: 'orange',
        background: '#fff7ed',
        border: '#fdba74',
        text: '#9a3412',
      },
    },
    {
      id: 'slack',
      name: 'Slack',
      ref: 'slack',
      description: 'Fixture provider for success notification review.',
      auth: {supported: ['oauth'], default: 'oauth'},
      palette: {
        color: 'purple',
        background: '#f5f3ff',
        border: '#c4b5fd',
        text: '#5b21b6',
      },
    },
  ],
};

const fixtureNodesCatalog: ConnectNodeCatalog = {
  version: 'v1',
  nodes: [
    {
      paletteId: 'fixture.manual',
      workflowType: 'trigger.manual',
      label: 'Manual trigger',
      description: 'Start the fixture run from a reviewed test payload.',
      category: 'Triggers',
      defaults: {
        type: 'trigger.manual',
        ref: 'fixture.manual',
        label: 'Manual trigger',
      },
    },
    {
      paletteId: 'hubspot.contacts.upsert',
      workflowType: 'api.connector',
      label: 'Upsert contact',
      description: 'Fixture HubSpot contact upsert action.',
      category: 'Integrations',
      providerId: 'hubspot',
      actions: ['contacts.upsert'],
      defaults: {
        type: 'api.connector',
        ref: 'hubspot.contacts.upsert',
        label: 'Upsert contact',
        connection: 'hubspot-prod',
      },
    },
    {
      paletteId: 'system.normalize_payload',
      workflowType: 'control.transform',
      label: 'Normalize payload',
      description: 'Normalize CRM payload fields before routing.',
      category: 'Logic',
      defaults: {
        type: 'control.transform',
        ref: 'system.normalize_payload',
        label: 'Normalize payload',
      },
    },
    {
      paletteId: 'slack.messages.send',
      workflowType: 'api.connector',
      label: 'Send Slack message',
      description: 'Fixture Slack success notification.',
      category: 'Integrations',
      providerId: 'slack',
      actions: ['messages.send'],
      defaults: {
        type: 'api.connector',
        ref: 'slack.messages.send',
        label: 'Notify success',
      },
    },
  ],
};

function currentFixtureState(): ConnectWorkflowCanvasVisualFixtureState {
  const href = typeof window !== 'undefined' && window.location
    ? window.location.href
    : 'http://localhost/connect/workflows/review-fixture/fixture-debug-workflow/run/fixture-run?connectWorkflowVisualFixture=succeeded';
  return readConnectWorkflowCanvasVisualFixture(href)?.state ?? 'succeeded';
}

function cloneWorkflow(id: string): ConnectWorkflow {
  return {
    ...fixtureWorkflow,
    id,
    nodes: fixtureWorkflow.nodes?.map(node => ({
      ...node,
      ['x-ui']: node['x-ui'] ? {...node['x-ui']} : undefined,
    })),
    edges: fixtureWorkflow.edges?.map(edge => ({...edge})),
    tags: [...(fixtureWorkflow.tags ?? [])],
    ['x-ui']: {
      ...(fixtureWorkflow['x-ui'] ?? {}),
      backgrounds: fixtureWorkflow['x-ui']?.backgrounds?.map(background => ({
        ...background,
        position: {...background.position},
        size: {...background.size},
        style: background.style ? {...background.style} : undefined,
      })),
    },
  };
}

function fixtureRunForState(state: ConnectWorkflowCanvasVisualFixtureState, runId: string, workflowId: string): ConnectRun {
  if (state === 'failed') {
    return {
      id: runId,
      workflow_id: workflowId,
      workflow_version: 7,
      status: 'failed',
      created_at: '2026-07-01T09:20:00Z',
      started_at: '2026-07-01T09:20:04Z',
      finished_at: '2026-07-01T09:20:31Z',
      duration_ms: 27_000,
      attempt: 1,
      retries: 1,
      steps: [
        {
          id: 'step-trigger',
          node_id: 'node-trigger',
          type: 'trigger.manual',
          status: 'succeeded',
          attempt: 1,
          duration_ms: 22,
          started_at: '2026-07-01T09:20:04Z',
          finished_at: '2026-07-01T09:20:04Z',
          input: {initiator: 'fixture-review'},
          output: {accepted: true},
        },
        {
          id: 'step-hubspot',
          node_id: 'node-hubspot',
          type: 'api.connector',
          status: 'succeeded',
          attempt: 1,
          duration_ms: 1_820,
          started_at: '2026-07-01T09:20:05Z',
          finished_at: '2026-07-01T09:20:07Z',
          input: {contact_id: '123'},
          output: {crm_status: 'needs_retry'},
        },
        {
          id: 'step-transform',
          node_id: 'node-transform',
          type: 'control.transform',
          status: 'succeeded',
          attempt: 1,
          duration_ms: 180,
          started_at: '2026-07-01T09:20:08Z',
          finished_at: '2026-07-01T09:20:08Z',
          input: {crm_status: 'needs_retry'},
          output: {retryable: true},
        },
        {
          id: 'step-failure',
          node_id: 'node-failure',
          type: 'api.http',
          status: 'failed',
          attempt: 2,
          retries: 1,
          duration_ms: 12_400,
          started_at: '2026-07-01T09:20:11Z',
          finished_at: '2026-07-01T09:20:23Z',
          input: {destination: 'https://review.example/hooks'},
          output: null,
          error: {
            code: 'fixture_timeout',
            message: 'Connector rejected payload after retry budget was exhausted.',
            retryable: false,
          },
        },
      ],
    };
  }

  if (state === 'empty') {
    return {
      id: runId,
      workflow_id: workflowId,
      workflow_version: 7,
      status: 'succeeded',
      created_at: '2026-07-01T08:50:00Z',
      started_at: '2026-07-01T08:50:01Z',
      finished_at: '2026-07-01T08:50:03Z',
      duration_ms: 2_000,
      attempt: 1,
      retries: 0,
      steps: [],
    };
  }

  return {
    id: runId,
    workflow_id: workflowId,
    workflow_version: 7,
    status: 'succeeded',
    created_at: '2026-07-01T09:02:00Z',
    started_at: '2026-07-01T09:02:02Z',
    finished_at: '2026-07-01T09:02:21Z',
    duration_ms: 19_000,
    attempt: 1,
    retries: 0,
    steps: [
      {
        id: 'step-trigger',
        node_id: 'node-trigger',
        type: 'trigger.manual',
        status: 'succeeded',
        attempt: 1,
        duration_ms: 20,
        started_at: '2026-07-01T09:02:02Z',
        finished_at: '2026-07-01T09:02:02Z',
        input: {initiator: 'fixture-review'},
        output: {accepted: true},
      },
      {
        id: 'step-hubspot',
        node_id: 'node-hubspot',
        type: 'api.connector',
        status: 'succeeded',
        attempt: 1,
        duration_ms: 1_420,
        started_at: '2026-07-01T09:02:04Z',
        finished_at: '2026-07-01T09:02:05Z',
        input: {contact_id: '123'},
        output: {crm_status: 'ok'},
      },
      {
        id: 'step-transform',
        node_id: 'node-transform',
        type: 'control.transform',
        status: 'succeeded',
        attempt: 1,
        duration_ms: 210,
        started_at: '2026-07-01T09:02:06Z',
        finished_at: '2026-07-01T09:02:06Z',
        input: {crm_status: 'ok'},
        output: {routing: 'success'},
      },
      {
        id: 'step-success',
        node_id: 'node-success',
        type: 'api.connector',
        status: 'succeeded',
        attempt: 1,
        duration_ms: 960,
        started_at: '2026-07-01T09:02:08Z',
        finished_at: '2026-07-01T09:02:09Z',
        input: {channel: '#ops'},
        output: {message_id: 'msg-42'},
      },
    ],
  };
}

function fixtureContextForState(state: ConnectWorkflowCanvasVisualFixtureState): ConnectRunContext {
  if (state === 'empty') {
    return {};
  }

  return {
    manual: {
      contact_id: '123',
      source: 'fixture-review',
    },
    replay: {
      allowed: true,
      actor: 'codex-visual-review',
    },
    route: {
      branch: state === 'failed' ? 'failure' : 'success',
    },
  };
}

function fixtureReplayForState(state: ConnectWorkflowCanvasVisualFixtureState): ConnectRunReplaySummary {
  if (state === 'empty') {
    return {
      plan_version: 7,
      node_count: 6,
      edge_count: 5,
      event_count: 0,
      last_event_type: '',
      event_counts: {},
    };
  }

  if (state === 'failed') {
    return {
      plan_version: 7,
      node_count: 6,
      edge_count: 5,
      event_count: 6,
      last_event_type: 'step.failed',
      event_counts: {
        'run.started': 1,
        'step.failed': 1,
        'step.succeeded': 3,
        'run.failed': 1,
      },
    };
  }

  return {
    plan_version: 7,
    node_count: 6,
    edge_count: 5,
    event_count: 6,
    last_event_type: 'run.completed',
    event_counts: {
      'run.started': 1,
      'step.succeeded': 4,
      'run.completed': 1,
    },
  };
}

@Injectable()
class ConnectWorkflowCanvasVisualFixtureWorkflowsService {
  listWorkflows(): Observable<ConnectWorkflow[]> {
    return of([cloneWorkflow('fixture-debug-workflow')]);
  }

  getWorkflow(id: string): Observable<ConnectWorkflow> {
    return of(cloneWorkflow(id || 'fixture-debug-workflow'));
  }

  createWorkflow(workflow: ConnectWorkflow): Observable<ConnectWorkflow> {
    return of({...workflow, id: workflow.id || 'fixture-debug-workflow'});
  }

  updateWorkflow(id: string, workflow: ConnectWorkflow): Observable<ConnectWorkflow> {
    return of({...workflow, id});
  }

  runWorkflow(id: string): Observable<ConnectWorkflowRun> {
    return of({
      runId: 'fixture-run-succeeded',
      workflowId: id,
      status: 'queued',
      enqueuedAt: '2026-07-01T09:30:00Z',
    });
  }

  listRuns(workflowId?: string): Observable<ConnectRun[]> {
    return of([fixtureRunForState(currentFixtureState(), 'fixture-run', workflowId || 'fixture-debug-workflow')]);
  }

  getRun(runId: string): Observable<ConnectRun> {
    const state = currentFixtureState();
    if (state === 'error') {
      return throwError(() => new Error('Fixture run load failed.'));
    }
    return of(fixtureRunForState(state, runId || 'fixture-run', 'fixture-debug-workflow'));
  }

  getRunContext(): Observable<ConnectRunContext> {
    const state = currentFixtureState();
    if (state === 'error') {
      return throwError(() => new Error('Fixture context load failed.'));
    }
    return of(fixtureContextForState(state));
  }

  getRunReplay(): Observable<ConnectRunReplaySummary> {
    const state = currentFixtureState();
    if (state === 'error') {
      return throwError(() => new Error('Fixture replay load failed.'));
    }
    return of(fixtureReplayForState(state));
  }

  rerun(runId: string): Observable<{runId: string; status: string; createdAt: string}> {
    return of({
      runId,
      status: 'queued',
      createdAt: '2026-07-01T09:35:00Z',
    });
  }

  simulateNode(): Observable<{output: unknown}> {
    return of({
      output: {
        fixture: true,
        message: 'Local visual fixture simulation only.',
      },
    });
  }
}

@Injectable()
class ConnectWorkflowCanvasVisualFixtureProvidersService {
  fetchCatalog(): Observable<ConnectProviderCatalog> {
    return of(fixtureProvidersCatalog);
  }
}

@Injectable()
class ConnectWorkflowCanvasVisualFixtureConnectionsService {
  list(): Observable<ConnectServerConnection[]> {
    return of([
      {
        id: 'fixture-connection-hubspot',
        name: 'HubSpot production',
        ref: 'hubspot',
        provider_ref: 'hubspot',
        status: 'connected',
        connected: true,
        oauth_connected: true,
      },
      {
        id: 'fixture-connection-slack',
        name: 'Slack notifications',
        ref: 'slack',
        provider_ref: 'slack',
        status: 'connected',
        connected: true,
        oauth_connected: true,
      },
    ]);
  }
}

@Injectable()
class ConnectWorkflowCanvasVisualFixtureNodesService {
  fetchCatalog(): Observable<ConnectNodeCatalog> {
    return of(fixtureNodesCatalog);
  }
}

export const connectWorkflowCanvasVisualFixtureProviders: Provider[] = [
  {provide: ConnectWorkflowsService, useClass: ConnectWorkflowCanvasVisualFixtureWorkflowsService},
  {provide: ConnectProvidersService, useClass: ConnectWorkflowCanvasVisualFixtureProvidersService},
  {provide: ConnectConnectionsService, useClass: ConnectWorkflowCanvasVisualFixtureConnectionsService},
  {provide: ConnectNodesService, useClass: ConnectWorkflowCanvasVisualFixtureNodesService},
];
